import { NgModule, Optional, SkipSelf } from '@angular/core';
import { emailQuotationReactionLazyImports } from '@contracting/email-quotation-reaction-lazy';
import { EmailQuotationReactionStoreModule } from '@contracting/email-quotation-reaction-store';
import { ContractingEntityTemporaryLibraryModule } from '@contracting/entity';
import { API_PUBLIC_PREFIX, apiPublicBasePath } from '@core/config-angular';
import { AddressCoreModule } from '@essent/address';
import { API as contractAPI, ContractCoreModule } from '@essent/contract';
import { API as customerAPI, CustomerCoreModule } from '@essent/customer';
import {
  API as estimatedConsumptionAPI,
  EstimateConsumptionCoreModule,
} from '@essent/estimate-consumption';
import {
  API as installationAddressAPI,
  InstallationAddressCoreModule,
} from '@essent/installation-address';
import {
  API as newCustomerAPI,
  NewCustomerCoreModule,
} from '@essent/new-customer';
import { ApiClientOpenModule } from '@innogy/api-client';
import { BecomeACustomerBaseModule } from '@innogy/become-a-customer-base';
import { becomeACustomerLazyImports } from '@innogy/become-a-customer/lazy';
import { chatbotLazyImports } from '@innogy/chatbot/iadvize/lazy';
import { CommonUiServiceComponentsModule } from '@innogy/common-ui/service-components';
import { cookieSettingsLazyImports } from '@innogy/cookie-settings-lazy';
import { ChattingStoreModule } from '@innogy/core-chatting';
import { createOnlineAccountLazyImports } from '@innogy/create-online-account/lazy';
import { CreateOnlineAccountStoreModule } from '@innogy/create-online-account/store';
import { emobilityLeadOpenLazyImports } from '@innogy/emobility-lead/lazy';
import { EmobilityLeadStoreModule } from '@innogy/emobility-lead/store';
import { emobilityQuotationAcceptanceLazyImports } from '@innogy/emobility-quotation-acceptance/lazy';
import { EmobilityQuotationAcceptanceStoreModule } from '@innogy/emobility-quotation-acceptance/store';
import { emobilityAppStoreRedirectLazyImports } from '@innogy/emobility/app-store-redirect/lazy';
import { API as fehAPI, FehModule } from '@essent/feh';
import {
  BoilersBootstrapModule,
  boilersLazyImports,
} from '@innogy/feh/boilers';
import {
  HeatpumpsBootstrapModule,
  heatpumpsLazyImports,
} from '@innogy/feh/heatpumps';
import {
  GenericStoreBootstrapModule,
  genericStoreLazyImports,
} from '@innogy/feh/generic-store';
import {
  InsulationBootstrapModule,
  insulationLazyImports,
} from '@innogy/feh/insulation';
import {
  FehSharedBootstrapModule,
  fehSharedLazyImports,
} from '@innogy/feh/shared';
import {
  SustainabilityBootstrapModule,
  sustainabilityLazyImports,
} from '@innogy/feh/sustainability';
import { solarPanelsLazyImports } from '@innogy/feh/solar-panels';
import { fehSolarPanelsLazyImports } from '@feh/solar-panels-lazy';
import { fehHomebatteryLazyImports } from '@feh/homebattery-lazy';
import { fehStandaloneFormsLazyImports } from '@feh/standalone-forms-lazy';
import { FehHomebatteryStoreModule } from '@feh/homebattery-store';
import {
  SolarPaybackBootstrapModule,
  solarPaybackLazyImports,
} from '@innogy/feh/solar-payback';
import { knowledgeBaseLazyImports } from '@innogy/knowledge-base/lazy';
import { marketingHeadingLazyImports } from '@innogy/marketing-heading/lazy';
import { memberGetMemberLazyImports } from '@innogy/shared/member-get-member/lazy';
import { tariffBreakdownLazyImports } from '@innogy/shared/tariff-breakdown/lazy';
import { SharedTariffBreakdownStoreModule } from '@innogy/shared/tariff-breakdown/store';
import { sitecoreFormsLazyImports } from '@innogy/sitecore-forms/lazy';
import { SitecoreFormsStoreModule } from '@innogy/sitecore-forms/store';
import { tariffsInOpenLazyImports } from '@innogy/tariffs/lazy';
import { TariffsStoreModule } from '@innogy/tariffs/store';
import { searchLazyImports } from '@search/lazy';
import { RecaptchaModule } from 'ng-recaptcha';
import { blogLazyImports } from '@blog/lazy';
import { contentLazyImports } from '@content/lazy';
import { FehServicesStoreModule } from '@innogy/feh/services';
import { FehStandaloneFormsStoreModule } from '@feh/standalone-forms-store';
import { ReviewsEntityModule } from '@reviews/entity';
import { reviewsLazyImports } from '@reviews/lazy';
import { FehSharedApiStoreModule } from '@feh/shared-api';
import { FehSolarPanelsStoreModule } from '@feh/solar-panels-store';
import { SelectCommodityPipe } from '@innogy/become-a-customer/shared';
import { EffectsModule } from '@ngrx/effects';
import { KnowledgeBaseEffects } from '@innogy/knowledge-base/shared';
import { TemporaryCoreModulesModule } from '@innogy/feh/temporary-core-modules';
import { fehProductCardsLazyImports } from '@feh/product-cards-lazy';
import { SmartChargingStoreModule } from '@smart-charging/store';

@NgModule({
  imports: [
    BecomeACustomerBaseModule,
    CommonUiServiceComponentsModule,
    InstallationAddressCoreModule.forRoot(),
    NewCustomerCoreModule.forRoot(),
    ContractCoreModule.forRoot(),
    EstimateConsumptionCoreModule.forRoot(),
    CustomerCoreModule.forRoot(),
    AddressCoreModule.forRoot({ api: apiPublicBasePath }),
    EmobilityLeadStoreModule,
    FehModule.forRoot(),
    BoilersBootstrapModule,
    HeatpumpsBootstrapModule,
    FehServicesStoreModule,
    FehHomebatteryStoreModule,
    FehStandaloneFormsStoreModule,
    SustainabilityBootstrapModule,
    FehSolarPanelsStoreModule,
    GenericStoreBootstrapModule,
    InsulationBootstrapModule,
    SolarPaybackBootstrapModule,
    FehSharedBootstrapModule,
    CreateOnlineAccountStoreModule,
    ChattingStoreModule,
    SitecoreFormsStoreModule,
    SharedTariffBreakdownStoreModule,
    EmobilityQuotationAcceptanceStoreModule,
    RecaptchaModule,
    TariffsStoreModule,
    ApiClientOpenModule,
    EmailQuotationReactionStoreModule,
    ContractingEntityTemporaryLibraryModule,
    ReviewsEntityModule,
    FehSharedApiStoreModule,
    TemporaryCoreModulesModule,
    SelectCommodityPipe,
    EffectsModule.forFeature([KnowledgeBaseEffects]),
    SmartChargingStoreModule,
  ],
  providers: [
    {
      provide: installationAddressAPI,
      useExisting: API_PUBLIC_PREFIX,
    },
    {
      provide: contractAPI,
      useExisting: API_PUBLIC_PREFIX,
    },
    {
      provide: newCustomerAPI,
      useExisting: API_PUBLIC_PREFIX,
    },
    {
      provide: estimatedConsumptionAPI,
      useExisting: API_PUBLIC_PREFIX,
    },
    { provide: customerAPI, useExisting: API_PUBLIC_PREFIX },
    { provide: fehAPI, useExisting: API_PUBLIC_PREFIX },
  ],
})
export class CoreModulesOpenModule {
  public constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModulesOpenModule
  ) {
    if (parentModule) {
      throw new Error('CoreModulesOpenModule is imported more than once.');
    }
  }
}

export const OPEN_JSS_IMPORTS = [];
export const OPEN_JSS_LAZY_IMPORTS = [
  ...knowledgeBaseLazyImports,
  ...becomeACustomerLazyImports,
  ...emailQuotationReactionLazyImports,
  ...marketingHeadingLazyImports,
  ...chatbotLazyImports,
  ...reviewsLazyImports,
  ...emobilityLeadOpenLazyImports,
  ...boilersLazyImports,
  ...heatpumpsLazyImports,
  ...sustainabilityLazyImports,
  ...solarPanelsLazyImports,
  ...solarPaybackLazyImports,
  ...genericStoreLazyImports,
  ...fehSharedLazyImports,
  ...insulationLazyImports,
  ...createOnlineAccountLazyImports,
  ...sitecoreFormsLazyImports,
  ...memberGetMemberLazyImports,
  ...tariffBreakdownLazyImports,
  ...tariffsInOpenLazyImports,
  ...cookieSettingsLazyImports,
  ...searchLazyImports,
  ...emobilityQuotationAcceptanceLazyImports,
  ...blogLazyImports,
  ...contentLazyImports,
  ...fehHomebatteryLazyImports,
  ...fehSolarPanelsLazyImports,
  ...fehStandaloneFormsLazyImports,
  ...emobilityAppStoreRedirectLazyImports,
  ...fehProductCardsLazyImports,
];
